import Swiper from 'swiper'
import type { SwiperOptions } from 'swiper/types'
import { Navigation, Pagination, Autoplay, EffectFade } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import 'swiper/css/effect-fade'

Swiper.use([Navigation, Pagination, Autoplay, EffectFade]);

/**
 * Get the swiper variant from element classes
 */
function getSwiperVariant(element: HTMLElement): string | null {
  const classes = Array.from(element.classList);
  const variantClass = classes.find(cls => cls.startsWith('swiper--'));
  return variantClass ? variantClass.replace('swiper--', '') : null;
}

/**
 * Get navigation button selectors from data attributes
 */
function getNavigationSelectors(element: HTMLElement): { prev: string | null; next: string | null } {
  return {
    prev: element.dataset.swiperPrev || null,
    next: element.dataset.swiperNext || null,
  };
}

/**
 * Get slide count from element
 */
function getSlideCount(element: HTMLElement): number {
  return element.querySelectorAll('.swiper-slide').length;
}

/**
 * Setup click vs drag detection for carousel links
 */
// function setupCarouselDragDetection(element: HTMLElement): void {
//   let dragStart = false;
//   let clickStart = { x: 0, y: 0 };

//   element.addEventListener('mousedown', (e) => {
//     dragStart = true;
//     clickStart = { x: e.clientX, y: e.clientY };
//   });

//   element.addEventListener('mouseup', (e) => {
//     const clickEnd = { x: e.clientX, y: e.clientY };
//     const distance = Math.sqrt(
//       Math.pow(clickEnd.x - clickStart.x, 2) + Math.pow(clickEnd.y - clickStart.y, 2)
//     );

//     // If moved more than 5px, it's a drag - prevent link navigation
//     if (dragStart && distance > 5) {
//       const link = (e.target as HTMLElement).closest('.swiper-slide-link') as HTMLAnchorElement;
//       if (link) {
//         e.preventDefault();
//       }
//     }
//     dragStart = false;
//   });
// }

/**
 * Get swiper configuration based on variant
 */
function getVariantConfig(variant: string, element: HTMLElement): SwiperOptions | null {
  const slideCount = getSlideCount(element);
  const navigation = getNavigationSelectors(element);

  switch (variant) {
    case 'hero': {
      // Hero slider - fade or slide transition
      if (slideCount <= 1) return null;

      const effect = element.dataset.swiperEffect || 'slide';
      const config: SwiperOptions = {
        modules: effect === 'fade' ? [Navigation, EffectFade] : [Navigation],
        slidesPerView: 1,
        allowTouchMove: false,
        autoHeight: true,
        loop: slideCount > 1,
        speed: 600,
        navigation: navigation.prev && navigation.next ? {
          prevEl: navigation.prev,
          nextEl: navigation.next,
        } : false,
      };

      if (effect === 'fade') {
        config.effect = 'fade';
        config.fadeEffect = {
          crossFade: true,
        };
      }

      return config;
    }

    case 'gallery': {
      // Image Gallery: one image at a time, the arrows either side of a "1 / 5" count
      // (_gallery.scss). Swiper.use() above registers the modules; listing them here as well
      // would double every click.
      const countSelector = element.dataset.swiperPagination || null;

      return {
        loop: slideCount > 1,
        slidesPerView: 1,
        spaceBetween: 32,
        navigation: navigation.prev && navigation.next ? {
          prevEl: navigation.prev,
          nextEl: navigation.next,
        } : false,
        pagination: countSelector ? {
          el: countSelector,
          type: 'fraction',
        } : false,
      };
    }

    case 'featured': {
      // Content Type Slider 1: the active slide large in the middle, its neighbours small
      // either side (sized in _slider.scss), arrows and dots.
      const paginationSelector = element.dataset.swiperPagination || null;

      return {
        slidesPerView: 'auto',
        centeredSlides: true,
        spaceBetween: 32,
        initialSlide: Math.min(1, slideCount - 1),
        rewind: true,
        slideToClickedSlide: true,
        speed: 500,
        navigation: navigation.prev && navigation.next ? {
          prevEl: navigation.prev,
          nextEl: navigation.next,
        } : false,
        pagination: paginationSelector ? {
          el: paginationSelector,
          clickable: true,
          bulletElement: 'button',
        } : false,
      };
    }

    case 'cards': {
      // Content Type Slider 2-4: a row of cards from the container's left edge, running off
      // the right of the page.
      return {
        slidesPerView: 'auto',
        spaceBetween: element.closest('.slider--wide') ? 32 : 20,
        speed: 500,
        watchOverflow: true,
        navigation: navigation.prev && navigation.next ? {
          prevEl: navigation.prev,
          nextEl: navigation.next,
        } : false,
      };
    }

    case 'testimonial': {
      // Testimonials: one at a time, the arrows either side of the dots below (_testimonials.scss).
      if (slideCount <= 1) return null;
      const paginationSelector = element.dataset.swiperPagination || null;

      return {
        slidesPerView: 1,
        spaceBetween: 32,
        autoHeight: true,
        loop: slideCount >= 3,
        navigation: navigation.prev && navigation.next ? {
          prevEl: navigation.prev,
          nextEl: navigation.next,
        } : false,
        pagination: paginationSelector ? {
          el: paginationSelector,
          clickable: true,
          bulletElement: 'button',
        } : false,
      };
    }

    default:
      console.warn(`Unknown swiper variant: ${variant}`);
      return null;
  }
}

/**
 * Initialize all swiper instances on the page
 */
export function initSwipers(): void {
  const swipers = document.querySelectorAll('.swiper') as NodeListOf<HTMLElement>;

  swipers.forEach((swiperEl) => {
    // Skip if already initialized - check for swiper instance
    if ((swiperEl as any).swiper) {
      console.log('Swiper already initialized, skipping:', swiperEl);
      return;
    }

    const variant = getSwiperVariant(swiperEl);
    
    if (!variant) {
      console.warn('Swiper element found without variant class:', swiperEl);
      return;
    }

    const config = getVariantConfig(variant, swiperEl);
    
    if (!config) {
      // Config is null when conditions aren't met (e.g., single slide)
      return;
    }

    console.log('Initializing swiper:', variant, swiperEl);
    new Swiper(swiperEl, config);
  });
}
